import React from 'react';
import { TabContent, Tabs } from '@brighterion/br-ui';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { addAdditionalBreadcrumbParams } from 'Store';
import { resolveRuleType } from 'Utils';

import { useGetTenantGroupOverridesInherited } from 'Hooks/query-hooks/tenant-groups/useGetTenantGroupOverrides';
import useGlobalRules from 'Hooks/query-hooks/global-rules/useGlobalRules';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetTenantGroup from 'Hooks/query-hooks/tenant-groups/useGetTenantGroup';
import useGetTenantGroupRulesColumns from 'Hooks/useGetTenantGroupRulesColumns';
import useGetListNames from 'Hooks/query-hooks/list/useGetListNames';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';

import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import TenantGroupOverview from './components/TenantGroupOverview';
import TenantGroupRulesTable from './components/TenantGroupRulesTable';
import TenantGroupPageHeader from './components/TenantGroupPageHeader';
import TenantGroupTenants from './components/TenantGroupTenants/TenantGroupTenants';

import styles from './TenantGroup.css';

const TenantGroup = () => {
    const { tenantGroupId } = useParams<{ tenantGroupId: string }>();
    const numericTenantGroupId = Number(tenantGroupId);
    const tenantGroupAPI = useGetTenantGroup({ tenantGroupId });
    const { users } = useGetReduxUsers();
    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });
    const listNamesAPI = useGetListNames();
    const tenantGroupOverridesInheritedAPI = useGetTenantGroupOverridesInherited({
        users,
        tenantGroupId,
        queryEnabled:
            !listNamesAPI.isLoading &&
            !listNamesAPI.isFetching &&
            !parametersAPI.isLoading &&
            !parametersAPI.isFetching,
        listNameMap: listNamesAPI.data,
        parameterData: parametersAPI.data
    });
    const globalRulesAPI = useGlobalRules({
        enable:
            !listNamesAPI.isLoading &&
            !listNamesAPI.isFetching &&
            !parametersAPI.isLoading &&
            !parametersAPI.isFetching,
        listNameMap: listNamesAPI.data,
        parameterData: parametersAPI.data
    });
    const ruleTableColumns = useGetTenantGroupRulesColumns();
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const { hasError, errorCmp } = errorController([
        tenantGroupOverridesInheritedAPI.error,
        globalRulesAPI.error,
        tenantGroupAPI.error
    ]);

    const tenantGroupOverride = React.useMemo(() => {
        return {
            ...tenantGroupOverridesInheritedAPI.data,
            rules: tenantGroupOverridesInheritedAPI.data?.rules
                .filter((rule) => rule.inherit === 'TENANT_GROUP')
                .map((rule) => {
                    return { ...rule, ruleType: resolveRuleType(rule) };
                })
        };
    }, [tenantGroupOverridesInheritedAPI.data]);

    React.useEffect(() => {
        if (tenantGroupAPI.data) {
            dispatch(
                addAdditionalBreadcrumbParams({
                    pathname: location.pathname,
                    params: {
                        tenantGroupName: tenantGroupAPI.data?.name
                    }
                })
            );
        }
    }, [location, tenantGroupId, tenantGroupAPI.data, numericTenantGroupId, dispatch]);

    if (
        tenantGroupAPI.isLoading ||
        tenantGroupAPI.isFetching ||
        tenantGroupOverridesInheritedAPI.isLoading ||
        tenantGroupOverridesInheritedAPI.isFetching ||
        globalRulesAPI.isFetching ||
        globalRulesAPI.isLoading
    ) {
        return <RouteLoading />;
    }

    if (hasError) return <>{errorCmp}</>;

    return (
        <div className={styles.pageContainer}>
            <TenantGroupPageHeader
                tenantGroupName={tenantGroupAPI.data?.name}
                disableViewDeployed={!tenantGroupOverridesInheritedAPI.data?.isDeployed}
                tenantGroupId={tenantGroupId}
            />

            <TenantGroupOverview
                // @ts-ignore
                ruleData={tenantGroupOverride!}
                tenantGroupId={tenantGroupId}
                tenantGroup={tenantGroupAPI.data}
            />

            <div className={styles.tableAndTabContainer}>
                <Tabs className={styles.tab} startIndex={0} type='small'>
                    <TabContent headerTestId='rules-tab' hidden label={t('Rules')}>
                        <TenantGroupRulesTable
                            columnNames={ruleTableColumns}
                            globalRules={globalRulesAPI.data!}
                            // @ts-ignore
                            tenantOverrideRules={tenantGroupOverride!}
                            mode='sandbox'
                        />
                    </TabContent>
                    <TabContent headerTestId='tenants-tab' hidden label={t('Tenants')}>
                        <TenantGroupTenants tenantGroupId={tenantGroupId} tenantGroupName={tenantGroupAPI.data?.name} />
                    </TabContent>
                </Tabs>
            </div>
        </div>
    );
};

export default TenantGroup;
